import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import { CircleIcon } from "lucide-react";
import type * as React from "react";

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("grid gap-3", className)}
      {...props}
    />
  );
}

const radioGroupItemVariants = cva(
  cn(
    /* sorted */
    "shrink-0",
    "size-4",
    "cursor-pointer border rounded-full shadow-xs outline-none",
    "transition-[color,box-shadow]",
    "disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:text-text-dark-min",
  ),
  {
    variants: {
      variant: {
        primary: "text-text-primary",
        neutral: "text-text-dark-max",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);
const radioGroupItemIconVariants = cva(
  cn(
    /* sorted */
    "absolute top-1/2 left-1/2",
    "size-2",
    "rounded-full",
    "-translate-x-1/2 -translate-y-1/2",
  ),
  {
    variants: {
      variant: {
        primary: "fill-primary bg-bg-primary",
        neutral: "fill-dark-max bg-bg-dark-max",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);

function RadioGroupItem({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item> &
  VariantProps<typeof radioGroupItemVariants>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(radioGroupItemVariants({ variant }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className={cn(radioGroupItemIconVariants({ variant }))} />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem };
